import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  DocumentAttribute,
  MultiSelectField,
  UserReferenceField,
} from "../../../../../components/AttributeGroupFormGenerator/AttributeGroupFormGenerator";
import { ObjectReferenceField } from "../../../../../components/ObjectReference/ObjectReferenceField";
import { LinkAttributeField } from "../../../../../components/LinkAttributeField/LinkAttributeField";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import { DatePicker } from "../../../../../components/DatePicker/DatePicker";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { RichEditor } from "../../../../../components/RichEditor/RichEditor";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { CollectionSingleSelectListBackedField } from "../../../../../components/AttributeGroupFormGenerator/ListBackedSelectField";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { endpoints } from "../../../../../endpoints";
import { Form } from "../../../../../layout/FormLayout";
import type { OptionType, User, UUID } from "../../../../../types/types";
import type {
  AttributeSchema,
  CollectionRowSchema,
  DocumentAttributeValue,
  IobjectReferenceCollectionItem,
  IproductReferenceCollectionItem,
  PIMProduct,
} from "../../../../../types/types.PIM";
import {
  getUserName,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";
import { shouldUseLazyListChoices } from "../../../../../util/attributeChoices";
import { LinkAttributeValueSchema } from "../../../../../util/zod.util";
import { getValidationForProductCollection } from "../components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import { MultiSelectRefType } from "../components/ProductCollectionSchemaTable/type";
import {
  AddressValue,
  transformAddress,
} from "../SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { AddressAttributeForm } from "../SellerAdminPIMAttributes/AddressAttributes/AddressAttributeForm";

export function stringArrayToOptionArray(str: string): OptionType<string> {
  return { label: str, value: str };
}

function handleFormValues(
  val:
    | string
    | boolean
    | OptionType<string | null>
    | { display_text: string; url: string }
    | string[]
    | OptionType<string>[]
    | undefined
) {
  if (typeof val === "string") {
    return val ? [val] : null;
  } else if (val === undefined || val === null) {
    return null;
  } else if (typeof val === "boolean") {
    return [val];
  } else if (Array.isArray(val)) {
    if (val.length > 0 && typeof val[0] === "object") {
      return val.map((item: any) => (item?.value ? item.value : item));
    }
    return val;
  } else if (typeof val === "object") {
    const parsedLink = LinkAttributeValueSchema.safeParse(val);
    const parsedOptionType = z
      .object({
        value: z.union([z.string(), z.boolean()]),
        label: z.string(),
      })
      .safeParse(val);
    if (parsedLink.success) {
      return parsedLink.data.url ? [parsedLink.data] : null;
    }
    if (parsedOptionType.success) {
      return (val as OptionType)?.value ? [(val as OptionType).value] : null;
    }
    const isAddressValue =
      "address1" in val || "company_name" in val || "country_code" in val;
    if (isAddressValue) {
      const transformed = transformAddress(val as Record<string, any>);
      return transformed ? [transformed] : null;
    }
  } else if (typeof val === "number") {
    return [val];
  } else if (val === null) {
    return null;
  }
}

type FormOutput = {
  [key: string]: string | OptionType<string> | boolean;
};

const getCustomValidation = ({
  input_type,
  is_multiple_choice,
  optional,
  t,
}: {
  input_type: AttributeSchema["input_type"];
  is_multiple_choice: boolean;
  optional: boolean;
  t: TFunction;
}) => {
  return getValidationForProductCollection({
    input_type,
    is_multiple_choice,
    optional,
    t,
  });
};

const getValidationSchema = (columns: AttributeSchema[], t: TFunction) => {
  let validationSchemaObj: {
    [prop: string]:
      | z.ZodString
      | z.ZodBoolean
      | z.ZodSchema
      | z.ZodOptional<z.ZodString | z.ZodBoolean | z.ZodSchema>;
  } = {};

  columns.forEach((col) => {
    validationSchemaObj = {
      ...validationSchemaObj,
      [col.id]: getCustomValidation({
        input_type: col.input_type,
        is_multiple_choice: col.is_multiple_choice ?? false,
        optional: true,
        t,
      }),
    };
  });
  return z.object(validationSchemaObj).superRefine((formValue, ctx) => {
    if (
      Object.values(formValue).every(
        (value: string | boolean | string[] | OptionType<any> | undefined) => {
          if (typeof value === "string") {
            return value === "";
          } else if (Array.isArray(value)) {
            return value.length === 0;
          } else if (typeof value === "object") {
            return !value;
          } else {
            return value === undefined || value === null;
          }
        }
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("Select at least one field"),
        path: [],
      });
    }
  });
};

export function AddItemToTemplateCollectionForm({
  onSuccess,
  collectionID,
  columns,
  row,
  onCancel,
  shouldValidateForm = true,
}: {
  onSuccess: () => void;
  collectionID: UUID;
  columns: AttributeSchema[];
  row: CollectionRowSchema | null;
  onCancel: () => void;
  shouldValidateForm?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const {
    tenant_id,
    storefront_metadata: { generic_sku_packaging_type_name },
  } = useStoreState();
  const multiSelectRefs = useRef<{ [key: string]: MultiSelectRefType }>({});

  const defaultValues =
    row?.attributes.reduce((acc, row_attr) => {
      const has_value =
        !!row_attr.default_values && !!row_attr.default_values[0]?.value;
      if (row_attr.input_type === "link") {
        acc[row_attr.id] = has_value
          ? (row_attr.default_values![0]?.value as {
              url: string;
              display_text: string;
            })
          : { display_text: "", url: "" };
      } else if (row_attr.input_type === "toggle") {
        acc[row_attr.id] = has_value
          ? (row_attr.default_values![0]?.value as boolean)
          : false;
      } else if (row_attr.input_type === "single_select") {
        acc[row_attr.id] = {
          // This string coercion shouldn't be needed but typescript believes it
          // can still be a boolean here.
          label: has_value
            ? String(row_attr.default_values![0]?.enum_variant?.variant)
            : "",
          value: has_value
            ? row_attr.default_values![0]?.enum_variant?.variant
            : "",
        };
      } else if (row_attr.input_type === "product_reference") {
        acc[row_attr.id] = {
          label: has_value
            ? (
                row_attr.default_values![0]
                  ?.value as IproductReferenceCollectionItem
              ).name
            : "",
          value: has_value
            ? (
                row_attr.default_values![0]
                  ?.value as IproductReferenceCollectionItem
              ).id
            : "",
        };
      } else if (row_attr.input_type === "object_reference") {
        acc[row_attr.id] = {
          label: has_value
            ? (
                row_attr.default_values![0]
                  ?.value as IobjectReferenceCollectionItem
              ).name
            : "",
          value: has_value
            ? (
                row_attr.default_values![0]
                  ?.value as IobjectReferenceCollectionItem
              ).id
            : "",
        };
      } else if (row_attr.input_type === "user") {
        acc[row_attr.id] = {
          label: has_value
            ? getUserName(
                (row_attr.default_values![0]?.value as User).firstname,
                (row_attr.default_values![0]?.value as User).lastname
              )
            : "",
          value: has_value
            ? (row_attr.default_values![0]?.value as User).id
            : "",
        };
      } else if (row_attr.input_type === "multi_select") {
        acc[row_attr.id] =
          row_attr.default_values?.map((val) => val.value as string) || [];
      } else if (row_attr.input_type === "address") {
        acc[row_attr.id] = has_value
          ? (row_attr.default_values![0]?.value as AddressValue)
          : {};
      } else if (row_attr.input_type === "document") {
        acc[row_attr.id] = has_value
          ? row_attr
              .default_values!.filter(
                (dv) => (dv.value as DocumentAttributeValue)?.asset_id
              )
              .map((dv) => ({
                asset_id: (dv.value as DocumentAttributeValue).asset_id,
                value_id: dv.id,
              }))
          : [];
      } else {
        acc[row_attr.id] = String(
          has_value ? row_attr.default_values![0]?.value : ""
        );
      }

      return acc;
    }, {} as { [key: string]: OptionType | string | boolean | undefined | string[] | Record<string, unknown> | { display_text: string; url: string } | { asset_id: string; value_id: string }[] }) ??
    columns.reduce((acc, col) => {
      switch (col.input_type) {
        case "checkbox":
        case "toggle":
          acc[col.id] = false;
          break;
        case "single_select":
          acc[col.id] = { label: "", value: "" };
          break;
        case "product_reference":
          acc[col.id] = { label: "", value: "" };
          break;
        case "object_reference":
          acc[col.id] = { label: "", value: "" };
          break;
        case "user":
          acc[col.id] = { label: "", value: "" };
          break;
        case "multi_select":
          acc[col.id] = [];
          break;
        case "document":
          acc[col.id] = [];
          break;
        case "link":
          acc[col.id] = { display_text: "", url: "" };
          break;
        default:
          acc[col.id] = "";
      }
      return acc;
    }, {} as { [key: string]: OptionType | string | boolean | undefined | string[] | { display_text: string; url: string } });

  const methodsOfUseForm = useFormWrapper({
    resolver: shouldValidateForm
      ? zodResolver(getValidationSchema(row?.attributes || columns, t))
      : undefined,
    defaultValues: defaultValues,
    // mode: "onChange",
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState,
    errors,
    watch,
  } = methodsOfUseForm;

  const packagingTypeColId = columns?.find(
    (col) => col.object_type === "packaging_type"
  )?.id;

  const { notifyError, notifySuccess } = useContext(Notifications);

  const currentPackagingType = methodsOfUseForm.watch(packagingTypeColId);
  const isBulkPackaging = useMemo(() => {
    if (!packagingTypeColId) return false;
    return (
      (typeof currentPackagingType === "object"
        ? currentPackagingType?.value
        : currentPackagingType) === generic_sku_packaging_type_name
    );
  }, [
    packagingTypeColId,
    currentPackagingType,
    generic_sku_packaging_type_name,
  ]);

  useEffect(() => {
    if (isBulkPackaging && packagingTypeColId) {
      columns.forEach((col) => {
        if (col.object_type === "is_a_sample") {
          methodsOfUseForm.setValue(col.id, false);
        }
      });
    }
  }, [isBulkPackaging, columns, methodsOfUseForm, packagingTypeColId]);

  const createForm = (columns: AttributeSchema[]) => {
    // eslint-disable-next-line array-callback-return
    return columns.map((col) => {
      if (col.input_type === "form_field") {
        return (
          <TextField
            name={col.id}
            label={col.name}
            theref={register({
              required: false,
            })}
            formState={formState}
            errors={errors}
            type="text"
            key={col.id}
            readOnly={!col.is_editable}
          />
        );
      } else if (col.input_type === "single_select") {
        if (tenant_id && shouldUseLazyListChoices(col)) {
          const rowAttribute = row?.attributes.find(
            (attr) => attr.name === col.name
          );
          return (
            <CollectionSingleSelectListBackedField
              key={col.id}
              col={{
                ...col,
                default_values:
                  rowAttribute?.default_values ?? col.default_values,
                is_required: false,
              }}
              tenantId={tenant_id}
              defaultValue={
                (defaultValues?.[col.id] as OptionType<string> | undefined) ?? {
                  label: "",
                  value: "",
                }
              }
              methodsOfUseForm={methodsOfUseForm}
              errors={errors}
              t={t}
              is_single_select_disabled={!col.is_editable}
              dropdownZ={0}
              isPackagingTypeField={false}
              onPackagingTypeChange={(selectedValue, onChange) =>
                onChange(selectedValue)
              }
            />
          );
        }
        const choices = col?.choices?.map(stringArrayToOptionArray) ?? [];
        return (
          <Controller
            as={SelectBoxV2}
            control={control}
            name={col.id}
            isClearable
            disabled={!col.is_editable}
            placeholder={col.name}
            options={choices}
            rules={{
              required: false,
            }}
            errors={errors}
            formState={formState}
            key={col.id}
          />
        );
      } else if (col.input_type === "multi_select") {
        return (
          <MultiSelectField
            type={"collection"}
            attribute={
              row?.attributes.find((attr) => attr.name === col.name) || col
            }
            methodsOfUseForm={methodsOfUseForm}
            key={col.id}
            displayByName={true}
            multiSelectRefs={multiSelectRefs}
            isNotApplicable={col.is_not_applicable ?? false}
          />
        );
      } else if (col.input_type === "numeric") {
        return (
          <TextField
            name={col.id}
            label={`${col.name} (number)`}
            theref={register({
              required: false,
            })}
            formState={formState}
            errors={errors}
            type="number"
            key={col.id}
            readOnly={!col.is_editable}
          />
        );
      } else if (col.input_type === "multiline_entry") {
        return (
          <RichEditor
            value={(defaultValues?.[col.id] as string) ?? ""}
            id={col.id}
            name={col.id}
            label={col.name ?? (col.display_name ? t([col.display_name]) : "")}
            useFormMethods={{ register, formState, setValue, errors, watch }}
            key={col.id}
            readOnly={!col.is_editable}
          />
        );
      } else if (col.input_type === "date") {
        return (
          <DatePicker
            label={col.name ?? (col.display_name ? t([col.display_name]) : "")}
            name={col.id}
            defaultValue={defaultValues?.[col.id] as string}
            isOutsideRange={() => false}
            required={false}
            methodsOfUseForm={methodsOfUseForm}
            shouldValidate={true}
            key={col.id}
            disabled={!col.is_editable}
          />
        );
      } else if (col.input_type === "toggle" || col.input_type === "checkbox") {
        return (
          <ToggleSwitchV2
            label={col.name ?? (col.display_name ? t([col.display_name]) : "")}
            name={col.id}
            ref={register({ required: false })}
            key={col.id}
            disabled={
              !col.is_editable ||
              (col.object_type === "is_a_sample" && isBulkPackaging)
            }
          />
        );
      } else if (col.input_type === "product_reference") {
        return (
          <SearchSelectInfiniteScroll
            name={col.id}
            key={col.id}
            errors={errors}
            formState={formState}
            defaultValue={
              defaultValues?.[col.id] ?? {
                label: "",
                value: "",
              }
            }
            isClearable
            disabled={!col.is_editable}
            placeholder={col.display_name ? t([col.display_name]) : ""}
            baseUrl={endpoints.v2_tenants_id_pim_products_summary(tenant_id)}
            theref={register({
              name: col.id,
              required: col.is_required,
            })}
            params={(() => {
              const params = new URLSearchParams();
              params.append("order_by", "asc");
              params.append("published_products_only", "true");
              return params;
            })()}
            getOptions={(response: PIMProduct[]) =>
              response.reduce(
                (
                  prev: { label: string; value: string }[],
                  { name, id }: PIMProduct
                ) => [...prev, { label: name, value: id }],
                []
              )
            }
            onChange={(data: any) => setValue(col.id, data)}
            testid={col.id}
          />
        );
      } else if (col.input_type === "object_reference") {
        const rawDefaults =
          row?.attributes.find((attr) => attr.name === col.name)
            ?.default_values ??
          col.default_values ??
          [];
        const objectRefValues = rawDefaults.map((dv) => {
          if (dv.object_ref) return dv;
          const val = dv.value as
            | { id: string; name: string }
            | null
            | undefined;
          return val && typeof val === "object"
            ? { ...dv, object_ref: { id: val.id, name: val.name } }
            : dv;
        });
        return (
          <ObjectReferenceField
            attribute={
              {
                ...col,
                values: objectRefValues,
              } as Extract<AttributeSchema, { input_type: "object_reference" }>
            }
            methodsOfUseForm={methodsOfUseForm}
            isDisabled={!col.is_editable}
            isNotApplicable={col.is_not_applicable ?? false}
          />
        );
      } else if (col.input_type === "user") {
        return (
          <UserReferenceField
            attribute={col}
            methodsOfUseForm={methodsOfUseForm}
            isDisabled={!col.is_editable}
            isNotApplicable={col.is_not_applicable ?? false}
            isTemplate
          />
        );
      } else if (col.input_type === "address") {
        return (
          <FormProvider {...methodsOfUseForm}>
            <AddressAttributeForm
              key={col.id}
              fieldPrefix={col.id}
              mode={col.address_mode ?? "standard"}
              isNotApplicable={col.is_not_applicable ?? false}
              savedValue={defaultValues?.[col.id] as AddressValue}
              attributeName={
                col.display_name ? t([col.display_name]) : col.name
              }
            />
          </FormProvider>
        );
      } else if (col.input_type === "document") {
        const existingRowAttr = row?.attributes.find(
          (attr) => attr.name === col.name
        );
        const colWithDocValues = {
          ...col,
          values: existingRowAttr?.default_values ?? col.default_values ?? [],
        };
        return (
          <DocumentAttribute
            key={col.id}
            attribute={colWithDocValues}
            methodsOfUseForm={methodsOfUseForm}
            isDisabled={!col.is_editable}
            isNotApplicable={col.is_not_applicable ?? false}
          />
        );
      } else if (col.input_type === "link") {
        const maybeDefaultValue = (() => {
          if (defaultValues?.[col.id]) {
            const parsed = LinkAttributeValueSchema.safeParse(
              defaultValues?.[col.id]
            );
            if (parsed.success) {
              return parsed.data as { url: string; display_text: string };
            }
          }
          return { url: "", display_text: "" };
        })();

        return (
          <LinkAttributeField
            key={col.id}
            name={col.id}
            label={col.display_name ? t([col.display_name]) : col.name}
            control={control}
            register={register}
            formState={formState}
            errors={errors}
            defaultValue={maybeDefaultValue}
            isRequired={col.is_required}
            isDisabled={!col.is_editable}
            layout="vertical"
          />
        );
      }
    });
  };

  const onSubmit = async (values: FormOutput) => {
    // the order of the object keys after submission is not deterministic, in
    // order for the API call to succeed the rows must be in the same order as
    // the columns.
    setIsLoading(true);
    const formOutputInCorrectOrder = columns.reduce((acc, col) => {
      const id = row?.attributes.find(({ name }) => name === col.name)?.id;
      if (id) {
        acc[id] = values[id];
      } else if (col.id in values) {
        acc[col.id] = values[col.id];
      }
      return acc;
    }, {} as FormOutput);

    const formattedValues = Object.entries(formOutputInCorrectOrder).map(
      ([key, val]) => {
        return { attribute_id: key, default_values: handleFormValues(val) };
      }
    );

    const rowsToPatch = {
      rows: [formattedValues],
    };

    const indexesOfNonSupportedValues: number[] = [];

    for (let i = 0; i < columns.length; i++) {
      if (
        columns[i].input_type !== "form_field" &&
        columns[i].input_type !== "single_select" &&
        columns[i].input_type !== "product_reference" &&
        columns[i].input_type !== "object_reference" &&
        columns[i].input_type !== "user" &&
        columns[i].input_type !== "multi_select" &&
        columns[i].input_type !== "numeric" &&
        columns[i].input_type !== "multiline_entry" &&
        columns[i].input_type !== "checkbox" &&
        columns[i].input_type !== "date" &&
        columns[i].input_type !== "toggle" &&
        columns[i].input_type !== "link" &&
        columns[i].input_type !== "address" &&
        columns[i].input_type !== "document"
      ) {
        indexesOfNonSupportedValues.push(i);
      }
    }

    indexesOfNonSupportedValues.forEach((idx) => {
      let attribute_id = formattedValues[idx].attribute_id;
      formattedValues.splice(idx, 0, { attribute_id, default_values: null });
    });

    try {
      // If a row is passed in we're editing that row, otherwise we're creating
      // a new row
      if (row) {
        const promises = rowsToPatch.rows[0].reduce(
          (acc, { attribute_id, default_values }) => {
            acc.push(
              axios.patch(
                `/v2/tenants/${tenant_id}/pim/collections/${collectionID}/rows/attributes/${attribute_id}`,
                {
                  default_values: default_values ? default_values : [],
                }
              )
            );
            return acc;
          },
          [] as Promise<AxiosResponse<any>>[]
        );
        await Promise.all(promises);
        onSuccess();
        notifySuccess(t("Item edited successfully"));
      } else {
        await axios.patch(
          `/v2/tenants/${tenant_id}/pim/collections/${collectionID}`,
          rowsToPatch
        );
        notifySuccess(t("Item added successfully"));
        setIsLoading(false);
        onSuccess();
      }
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage ? errorMessage : t("There was an error adding values"),
        {
          error,
        }
      );
      setIsLoading(false);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {createForm(row?.attributes || columns)}
        <ButtonContainer style={{ marginTop: "30px" }}>
          <TertiaryButtonLargeV2 fullWidth type="reset" onClick={onCancel}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 fullWidth loading={isLoading} type="submit">
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </>
  );
}
